import React from 'react';
import {
  Box,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import {
  Category as CategoryIcon,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import type { StatusCategory } from '../../../../shared/types/config/status-builder.types';
import {
  borderRadius,
  componentSizes,
  getSemanticSurfaceTokens,
  layoutGap,
} from '../../../../theme/theme';
import type { SelectedNode } from '../../lib/selectionTreeModel';
import { useStatusBuilderNavigation } from '../../hooks/useStatusBuilderNavigation';
import { BodyText } from '../../../../shared/ui/typography';

interface StatusBuilderNavigationProps {
  selectedNode: SelectedNode;
  collapsed: boolean;
  isMobile: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onNodeSelect: (type: string, name?: string) => void;
  statusBuilderNavRef?: React.RefObject<HTMLDivElement | null>;
  statusCategoryFirstNavRef?: React.RefObject<HTMLDivElement | null>;
}

const getCategoryColor = (categoryColor: string, theme: Theme): string => {
  if (categoryColor === 'success') return theme.palette.success.main;
  if (categoryColor === 'error') return theme.palette.error.main;
  if (categoryColor === 'warning') return theme.palette.warning.main;
  if (categoryColor === 'info') return theme.palette.info.main;
  if (categoryColor === 'secondary') return theme.palette.secondary.main;
  return theme.palette.grey[500];
};

type StatusBuilderHeaderProps = Pick<
  StatusBuilderNavigationProps,
  'collapsed' | 'isMobile' | 'isExpanded' | 'onToggle' | 'onNodeSelect' | 'statusBuilderNavRef'
> & { selected: boolean };

const StatusBuilderHeader: React.FC<StatusBuilderHeaderProps> = ({
  collapsed,
  isMobile,
  isExpanded,
  onToggle,
  onNodeSelect,
  statusBuilderNavRef,
  selected,
}) => {
  const theme = useTheme();
  const colors = getSemanticSurfaceTokens(theme);
  return (
    <ListItemButton
      ref={statusBuilderNavRef}
      data-testid="mapping-tour-target-status-builder-nav"
      onClick={collapsed ? () => onNodeSelect('statusBuilder') : onToggle}
      sx={{
        pl: collapsed ? 0.5 : (isMobile ? 1.5 : 2),
        py: isMobile ? 0.5 : 0.25,
        mx: 0.25,
        mb: 0.125,
        borderRadius: `${borderRadius.sm}px`,
        backgroundColor: selected ? colors.background.selected : 'transparent',
        justifyContent: collapsed ? 'center' : 'flex-start',
        '&:hover': { backgroundColor: colors.background.hover },
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: collapsed ? componentSizes.treeIcon : (isMobile ? 32 : componentSizes.treeIcon + 4),
          color: selected ? theme.palette.primary.main : colors.text.tertiary,
          justifyContent: collapsed ? 'center' : 'flex-start',
        }}
      >
        <CategoryIcon sx={{ fontSize: isMobile ? 18 : componentSizes.icon }} />
      </ListItemIcon>
      {!collapsed ? (
        <>
          <ListItemText
            primary={
              <BodyText
                secondary
                sx={{
                  fontWeight: selected ? 600 : 400,
                  color: selected ? colors.text.primary : colors.text.secondary,
                }}
              >
                Project phase statuses
              </BodyText>
            }
          />
          <Box
            onClick={(event) => {
              event.stopPropagation();
              onToggle();
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: colors.text.secondary,
              cursor: 'pointer',
              p: layoutGap.micro,
              borderRadius: `${borderRadius.sm}px`,
              '&:hover': { backgroundColor: colors.background.hover },
            }}
          >
            {isExpanded ? (
              <ExpandLess sx={{ fontSize: isMobile ? 22 : '0.75rem' }} />
            ) : (
              <ExpandMore sx={{ fontSize: isMobile ? 22 : '0.75rem' }} />
            )}
          </Box>
        </>
      ) : null}
    </ListItemButton>
  );
};

interface StatusCategoryNavigationProps {
  categories: StatusCategory[];
  selectedNode: SelectedNode;
  isMobile: boolean;
  isLoading: boolean;
  error: string | null;
  onNodeSelect: (type: string, name?: string) => void;
  firstItemRef?: React.RefObject<HTMLDivElement | null>;
}

const StatusCategoryNavigation: React.FC<StatusCategoryNavigationProps> = ({
  categories,
  selectedNode,
  isMobile,
  isLoading,
  error,
  onNodeSelect,
  firstItemRef,
}) => {
  const theme = useTheme();
  const colors = getSemanticSurfaceTokens(theme);
  return (
    <List component="div" disablePadding>
      {categories.map((category, categoryIndex) => {
        const selected = selectedNode.type === 'statusCategory' && selectedNode.name === category.id;
        return (
          <ListItemButton
            key={category.id}
            ref={categoryIndex === 0 ? firstItemRef : undefined}
            data-testid={categoryIndex === 0 ? 'mapping-tour-target-status-category-nav' : undefined}
            selected={selected}
            onClick={() => onNodeSelect('statusCategory', category.id)}
            sx={{
              pl: 1.5,
              py: isMobile ? 0.5 : 0.25,
              mx: 0.25,
              mb: 0.125,
              borderRadius: `${borderRadius.sm}px`,
              backgroundColor: selected ? colors.background.selected : 'transparent',
              borderLeft: selected ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
              '&:hover': { backgroundColor: colors.background.hover },
            }}
          >
            <Box
              sx={{
                width: isMobile ? 10 : 8,
                height: isMobile ? 10 : 8,
                borderRadius: '50%',
                flexShrink: 0,
                backgroundColor: getCategoryColor(category.color, theme),
                mr: 0.5,
              }}
            />
            <ListItemText
              primary={
                <BodyText
                  secondary
                  sx={{
                    fontWeight: selected ? 600 : 400,
                    color: selected ? colors.text.primary : colors.text.secondary,
                  }}
                >
                  {category.name}
                </BodyText>
              }
            />
          </ListItemButton>
        );
      })}
      {categories.length === 0 && (isLoading || error) ? (
        <BodyText
          role="status"
          sx={{ px: 2, py: 0.5, color: colors.text.tertiary }}
        >
          {isLoading ? 'Loading Revit project phase statuses…' : 'Revit project phase statuses are unavailable.'}
        </BodyText>
      ) : null}
    </List>
  );
};

const StatusBuilderNavigation: React.FC<StatusBuilderNavigationProps> = ({
  selectedNode,
  collapsed,
  isMobile,
  isExpanded,
  onToggle,
  onNodeSelect,
  statusBuilderNavRef,
  statusCategoryFirstNavRef,
}) => {
  const { categories, isLoading, error } = useStatusBuilderNavigation({
    selectedNode,
    onNodeSelect,
  });
  const statusBuilderSelected =
    selectedNode.type === 'statusBuilder' || selectedNode.type === 'statusCategory';

  return (
    <>
      <StatusBuilderHeader
        selected={statusBuilderSelected}
        collapsed={collapsed}
        isMobile={isMobile}
        isExpanded={isExpanded}
        onToggle={onToggle}
        onNodeSelect={onNodeSelect}
        statusBuilderNavRef={statusBuilderNavRef}
      />
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <StatusCategoryNavigation
          categories={categories}
          selectedNode={selectedNode}
          isMobile={isMobile}
          isLoading={isLoading}
          error={error}
          onNodeSelect={onNodeSelect}
          firstItemRef={statusCategoryFirstNavRef}
        />
      </Collapse>
    </>
  );
};

export default StatusBuilderNavigation;
